import { useEffect, useState } from "react";
import { CheckCircle2, Wallet, Zap, Rocket } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const PACK_ICONS = { starter: Wallet, growth: Zap, scale: Rocket };

export default function Billing() {
  const { activeWorkspace, user } = useAuth();
  const [packs, setPacks] = useState(null);
  const [credits, setCredits] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [busyKey, setBusyKey] = useState(null);

  const load = () => {
    api.get("/billing/packs").then(({ data }) => setPacks(data));
    if (activeWorkspace) api.get("/billing/credits").then(({ data }) => setCredits(data));
  };
  useEffect(load, [activeWorkspace]);

  const paymentsEnabled = credits?.razorpay_configured !== false;

  const buy = async (amountInr, key) => {
    setBusyKey(key);
    try {
      const { data } = await api.post("/billing/topup", { amount_inr: amountInr });
      await openCheckout(data, amountInr);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Unable to process top-up");
    } finally {
      setBusyKey(null);
    }
  };

  const loadCheckoutScript = () =>
    new Promise((resolve, reject) => {
      if (window.Razorpay) return resolve();
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = resolve;
      script.onerror = () => reject(new Error("Could not load the Razorpay checkout"));
      document.body.appendChild(script);
    });

  const openCheckout = async (order, amountInr) => {
    await loadCheckoutScript();
    const rzp = new window.Razorpay({
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      order_id: order.order_id,
      name: "hireqix Resume Screener",
      description: `${amountInr} credits top-up`,
      image: "/images/hireqix-logo.webp",
      prefill: { name: user?.name || "", email: user?.email || "" },
      notes: { workspace: activeWorkspace?.name || "" },
      theme: { color: "#062A72" },
      handler: async (response) => {
        try {
          const { data } = await api.post("/billing/verify", {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
          if (data.already_credited) toast.success("This payment was already credited.");
          else toast.success(`Payment successful — ${data.credits_added} credits and ${data.job_bonus} job slots added`);
          load();
        } catch (err) {
          toast.error(
            `${formatApiError(err.response?.data?.detail)} Payment ID ${response.razorpay_payment_id} — email support@hireqix.com if credits don't appear.`
          );
        }
      },
      modal: {
        ondismiss: () => toast.info("Payment cancelled — no money was charged."),
      },
    });
    rzp.on("payment.failed", (resp) => {
      toast.error(resp?.error?.description || "Payment failed. No credits were added.");
    });
    rzp.open();
  };

  const buyCustom = () => {
    const amt = Number(customAmount);
    if (!amt || amt < (packs?.min_topup_inr || 500)) {
      toast.error(`Minimum top-up is ₹${packs?.min_topup_inr || 500}`);
      return;
    }
    buy(amt, "custom");
  };

  if (!packs) return null;

  const pct = credits?.percentage_remaining;
  const barColor = credits?.alert_level === "critical" ? "bg-destructive" : credits?.alert_level === "warning" ? "bg-amber-500" : "bg-primary";

  return (
    <div className="mx-auto max-w-4xl animate-in fade-in-0 slide-in-from-bottom-2 duration-500">
      <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Billing & Credits</h1>
      <p className="mt-1 text-sm text-muted-foreground">Credits never expire. Each resume evaluated costs {packs.resume_eval_cost_credits} credits.</p>

      <div className="mt-6 rounded-2xl border border-border/80 bg-card p-6 shadow-sm" data-testid="credit-balance-card">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Current Balance</div>
            <div className="mt-1 font-mono text-3xl font-bold" data-testid="credit-balance-value">{credits?.credits_balance ?? "—"} credits</div>
          </div>
          <div className="text-right text-sm text-muted-foreground">
            <div>Job slots used: <span className="font-mono font-medium text-foreground">{credits?.jobs_used ?? 0} / {credits?.max_jobs ?? 0}</span></div>
            <div className="mt-0.5">Lifetime purchased: <span className="font-mono">{credits?.credits_purchased_total ?? 0}</span></div>
          </div>
        </div>
        {pct !== null && pct !== undefined && (
          <div className="mt-4">
            <div className="h-2 overflow-hidden rounded-full bg-muted">
              <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${Math.max(pct, 2)}%` }} />
            </div>
            <div className="mt-1.5 text-xs text-muted-foreground">{pct}% of purchased credits remaining</div>
          </div>
        )}
      </div>

      <h2 className="mt-8 font-heading text-lg font-semibold">Buy Credits</h2>
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        {Object.entries(packs.packs).map(([key, pack]) => {
          const Icon = PACK_ICONS[key] || Wallet;
          return (
            <div key={key} className={`rounded-2xl border p-6 shadow-sm ${key === "scale" ? "border-primary" : "border-border/80"}`} data-testid={`credit-pack-${key}`}>
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
                <Icon className="h-4 w-4 text-primary" />
              </div>
              <div className="mt-3 font-mono text-2xl font-bold">₹{pack.price_inr}</div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground capitalize">{key} pack</div>
              <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> {pack.credits} credits</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> ~{pack.resume_estimate} resumes</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> +{pack.max_jobs_bonus} job slots</li>
              </ul>
              <Button
                onClick={() => buy(pack.price_inr, key)}
                disabled={busyKey === key || !paymentsEnabled}
                className="mt-5 w-full"
                data-testid={`buy-pack-btn-${key}`}
              >
                {busyKey === key ? "Processing..." : paymentsEnabled ? "Buy Now" : "Unavailable"}
              </Button>
            </div>
          );
        })}
      </div>

      <div className="mt-6 rounded-2xl border border-border/80 bg-card p-6 shadow-sm">
        <h3 className="font-heading font-semibold">Custom Top-Up</h3>
        <p className="mt-1 text-sm text-muted-foreground">Add any amount, minimum ₹{packs.min_topup_inr}.</p>
        <div className="mt-4 flex items-center gap-3">
          <Input
            type="number"
            min={packs.min_topup_inr}
            placeholder={`e.g. ${packs.min_topup_inr}`}
            value={customAmount}
            onChange={(e) => setCustomAmount(e.target.value)}
            data-testid="custom-topup-input"
            className="max-w-[200px]"
          />
          <Button onClick={buyCustom} disabled={busyKey === "custom" || !paymentsEnabled} variant="outline" data-testid="custom-topup-btn">
            {busyKey === "custom" ? "Processing..." : "Add Credits"}
          </Button>
        </div>
      </div>

      {!paymentsEnabled && (
        <p className="mt-4 rounded-xl border border-amber-500/40 bg-amber-500/5 p-4 text-xs text-muted-foreground" data-testid="razorpay-not-configured-note">
          Online payments aren't live yet, so credit purchases are disabled. Email{" "}
          <a href="mailto:support@hireqix.com" className="font-medium text-primary hover:underline">support@hireqix.com</a>{" "}
          and we'll add credits to your workspace manually.
        </p>
      )}
    </div>
  );
}
